import { useCallback } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useGymFinancials, useGymClasses, useGymMetricsConfig, useGymExtendedKPIs, useGymUserGrowth, useGymFinancialMetrics } from "@/hooks/gym-stats";
import { DynamicKPICards } from "./DynamicKPICards";
import { RevenueChart } from "./RevenueChart";
import { ExpiringMemberships } from "./ExpiringMemberships";
import { PopularClasses } from "./PopularClasses";
import { UserGrowthChart } from "./UserGrowthChart";
import { ClassOccupancyCard } from "./ClassOccupancyCard";
import { MetricsSelector } from "./MetricsSelector";
import { FinancialKPICards } from "./FinancialKPICards";
import { PaymentDistributionChart } from "./PaymentDistributionChart";
import { RevenueByPlanCard } from "./RevenueByPlanCard";
import { MultiDraggableGrid, DroppableArea } from "./DraggableMetricsGrid";
import { DraggableVisualizationCard } from "./DraggableVisualizationCard";
import { EditModeToggle } from "./EditModeToggle";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign } from "lucide-react";

interface GymStatsDashboardProps {
  gymId: string;
  gymName: string;
}

const LoadingSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-16" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-32" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Skeleton className="h-80" />
      <Skeleton className="h-80" />
    </div>
  </div>
);

export const GymStatsDashboard = ({ gymId, gymName }: GymStatsDashboardProps) => {
  const { metrics, toggleMetric, resetToDefaults, isMetricEnabled, reorderMetrics, getOrderedEnabledMetrics, isEditMode, toggleEditMode } = useGymMetricsConfig(gymId);
  const { data: kpis, isLoading: kpisLoading } = useGymExtendedKPIs(gymId);
  const { data: financials, isLoading: financialsLoading } = useGymFinancials(gymId);
  const { data: classesData, isLoading: classesLoading } = useGymClasses(gymId);
  const { data: userGrowth, isLoading: userGrowthLoading } = useGymUserGrowth(gymId);
  const { data: financialMetrics, isLoading: financialMetricsLoading } = useGymFinancialMetrics(gymId);

  const isLoading = kpisLoading || financialsLoading || classesLoading;

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    
    const { source, destination } = result;
    
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Determine category from droppableId
    const categoryMap: Record<string, "kpi" | "financial" | "visualization"> = {
      "kpi-cards": "kpi",
      "financial-kpi-cards": "financial",
      "visualizations": "visualization",
    };

    const category = categoryMap[source.droppableId];
    if (category) {
      reorderMetrics(source.index, destination.index, category);
    }
  }, [reorderMetrics]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get ordered enabled metrics by category
  const enabledKPIs = getOrderedEnabledMetrics("kpi");
  const enabledFinancialKPIs = getOrderedEnabledMetrics("financial")
    .filter(m => ["totalRevenue", "maxPayment", "revenuePerUser", "delinquencyRate", "monthlyProjection", "recoverableRevenue"].includes(m.id));
  const enabledVisualizations = getOrderedEnabledMetrics("visualization");

  // Build visualization components map
  const visualizationComponents: Record<string, React.ReactNode> = {
    revenueChart: financials && <RevenueChart data={financials.revenueByMonth} />,
    financialMetrics: (
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            Métricas Financieras
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {isMetricEnabled("averageTicket") && (
              <div className="p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Ticket Promedio</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(kpis?.averageTicket || 0)}
                </p>
              </div>
            )}
            {isMetricEnabled("totalClasses") && (
              <div className="p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Total Clases</p>
                <p className="text-2xl font-bold text-foreground">
                  {classesData?.totalClasses || 0}
                </p>
              </div>
            )}
            {isMetricEnabled("averageAttendance") && (
              <div className="p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Asistencia Promedio</p>
                <p className="text-2xl font-bold text-foreground">
                  {(classesData?.averageAttendance || 0).toFixed(0)}%
                </p>
              </div>
            )}
            {isMetricEnabled("previousMonthRevenue") && (
              <div className="p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Mes Anterior</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(kpis?.previousMonthRevenue || 0)}
                </p>
              </div>
            )}
            {isMetricEnabled("classOccupancy") && (
              <div className="p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Ocupación Clases</p>
                <p className="text-2xl font-bold text-foreground">
                  {(kpis?.classOccupancy || 0).toFixed(0)}%
                </p>
              </div>
            )}
            {isMetricEnabled("revenueGrowth") && (
              <div className="p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Crecimiento</p>
                <p className="text-2xl font-bold text-foreground">
                  {kpis?.revenueGrowth ? `${kpis.revenueGrowth > 0 ? '+' : ''}${kpis.revenueGrowth.toFixed(0)}%` : '0%'}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    ),
    paymentDistribution: financialMetrics && <PaymentDistributionChart data={financialMetrics.paymentDistribution} />,
    revenueByPlan: financialMetrics && <RevenueByPlanCard data={financialMetrics.revenueByPlan} />,
    expiringMemberships: financials && <ExpiringMemberships memberships={financials.expiringMemberships} />,
    popularClasses: classesData && <PopularClasses classes={classesData.popularClasses} />,
    userGrowthChart: userGrowth && !userGrowthLoading && <UserGrowthChart data={userGrowth} />,
    classOccupancyCard: <ClassOccupancyCard gymId={gymId} />,
  };

  return (
    <div className="space-y-6">
      {/* Header with Metrics Selector and Edit Toggle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <MetricsSelector
          metrics={metrics}
          onToggleMetric={toggleMetric}
          onReset={resetToDefaults}
        />
        <EditModeToggle isEditMode={isEditMode} onToggle={toggleEditMode} />
      </div>

      <MultiDraggableGrid onDragEnd={handleDragEnd}>
        {/* Dynamic KPI Cards */}
        {kpis && enabledKPIs.length > 0 && (
          <div className="mb-6">
            <DynamicKPICards kpis={kpis} enabledMetrics={enabledKPIs} isDraggable isEditMode={isEditMode} />
          </div>
        )}

        {/* Financial KPI Cards */}
        {financialMetrics && enabledFinancialKPIs.length > 0 && !financialMetricsLoading && (
          <div className="mb-6">
            <FinancialKPICards metrics={financialMetrics} enabledMetrics={enabledFinancialKPIs} isDraggable isEditMode={isEditMode} />
          </div>
        )}

        {/* Visualizations - Draggable Grid */}
        {enabledVisualizations.length > 0 && (
          <DroppableArea 
            droppableId="visualizations" 
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            direction="horizontal"
            isEditMode={isEditMode}
          >
            {enabledVisualizations.map((metric, index) => {
              const component = visualizationComponents[metric.id];
              if (!component) return null;
              
              return (
                <DraggableVisualizationCard key={metric.id} id={metric.id} index={index} isEditMode={isEditMode}>
                  {component}
                </DraggableVisualizationCard>
              );
            })}
          </DroppableArea>
        )}
      </MultiDraggableGrid>
    </div>
  );
};
